import { isFunction } from 'lodash'
import { omitBy } from 'lodash/fp'
import React, { cloneElement, ForwardedRef, isValidElement, MutableRefObject, Ref } from 'react'

export const componentAcceptsRef = (Component: any) => Component?.$$typeof === Symbol.for('react.forward_ref')

const PROVIDER_FOUND = true
const PROVIDER_NOT_FOUND = false

/**
 * This function takes ANY function and wraps it with try/catch,
 * returning [result, true] on success or [undefined, false] on provider missing error.
 *
 * @example
 * const [value, providerFound] = ignoreProviderMissingError(useSomeContext)
 * const [selectedValue, providerFound] = ignoreProviderMissingError(() => useContextWithSelect.select(s => s.field))
 */
export function ignoreProviderMissingError<TFn extends (...args: any[]) => any>(
  fn: TFn,
  {
    ignoreMessage,
    errorMessage,
    ignore = true
  }: {
    ignoreMessage?: string
    errorMessage?: string
    ignore?: boolean
  } = {}
): readonly [ReturnType<TFn> | undefined, boolean] {
  try {
    const result = fn()
    return [result, PROVIDER_FOUND] as const
  } catch (e: any) {
    if (
      ignore &&
      // react-tracked has this message
      (e.toString().includes('Please use <Provider>') ||
        // Constate has this message
        e.toString().includes('Component must be wrapped with Provider'))
    ) {
      if (ignoreMessage && process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn(ignoreMessage)
      }
      return [undefined, PROVIDER_NOT_FOUND]
    }
    if (errorMessage) e.message = `${e.message} ${errorMessage}`
    if (process.env.NODE_ENV !== 'production') throw e
    // eslint-disable-next-line no-console
    else console.error(e)
    return [undefined, PROVIDER_NOT_FOUND] as const
  }
}

/**
 * Assigns a value to a ref function or object
 *
 * @param ref the ref to assign to
 * @param value the value
 */
export function assignRef<T = any>(ref: ReactRef<T> | undefined, value: T) {
  if (ref == null) return

  if (isFunction(ref)) {
    ref(value)
    return
  }

  try {
    // @ts-ignore
    // eslint-disable-next-line no-param-reassign
    ref.current = value
  } catch {
    throw new Error(`Cannot assign value '${value}' to ref '${ref}'`)
  }
}

type ReactRef<T> = Ref<T> | MutableRefObject<T> | ForwardedRef<T>

/**
 * Combine multiple React refs into a single ref function.
 * This is used mostly when you need to allow consumers forward refs to
 * internal components
 *
 * @param refs refs to assign to value to
 */
export function mergeRefs<T>(...refs: (ReactRef<T> | undefined)[]) {
  return (node: T | null) => {
    refs.forEach(ref => assignRef(ref, node as T))
  }
}

/**
 * Returns true if currently executing within a React component or hook.
 * Used to enforce that certain utilities are NOT called from React context.
 *
 * This works by checking React's internal rendering state. The approach is
 * resilient to production/minified builds where function names are mangled.
 *
 * NOTE: This is a best-effort check using React internals. It works reliably
 * for the primary use case: detecting calls during component render phase.
 */
export function isWithinReactComponentOrHook(): boolean {
  try {
    // React 19 exposes internals under __CLIENT_INTERNALS_DO_NOT_USE_OR_WARN_USERS_THEY_CANNOT_UPGRADE,
    // where A = current owner fiber and H = current dispatcher.
    const internals = (React as any).__CLIENT_INTERNALS_DO_NOT_USE_OR_WARN_USERS_THEY_CANNOT_UPGRADE
    if (!internals) return false

    // Primary check: current owner fiber (set when rendering a component).
    const currentOwner = internals.A
    if (currentOwner != null) {
      return true
    }

    // Secondary check: current dispatcher (set during hook execution).
    const dispatcher = internals.H
    if (!dispatcher || typeof dispatcher.useState !== 'function') {
      return false
    }

    // In development builds, we can check the function name
    // Outside React, hooks are named "throwInvalidHookError"
    const hookName = dispatcher.useState?.name
    if (hookName) {
      const invalidNames = ['throwInvalidHookError', 'resolveDispatcher']
      if (invalidNames.includes(hookName)) {
        return false
      }
      // If it has a name and it's not an invalid one, we're likely in React
      // (unless minified, in which case we continue to other checks)
      if (hookName.length > 2) {
        // Non-minified name like "mountState" or "updateState"
        return true
      }
    }

    // For production/minified builds: if the owner fiber (internals.A) is null and the
    // dispatcher exists but only has minified names, we're likely outside React
    // (the dispatcher persists but the owner does not).
    return false
  } catch {
    // If any error occurs, assume we're outside React
    return false
  }
}

/**
 * Extracts the caller function name from a stack trace.
 * Skips internal frames to find the actual utility that called this.
 */
function getCallerFunctionName(skipFrames = 2): string {
  const stack = new Error().stack
  if (!stack) return 'Unknown function'

  const lines = stack.split('\n')
  // Skip: "Error", this function, assertNotInReactContext, then get the caller
  const callerLine = lines[skipFrames + 1]

  if (!callerLine) return 'Unknown function'

  // Stack format varies:
  // Chrome/Node: "    at functionName (file:line:col)" or "    at Object.functionName (file:line:col)"
  // Firefox: "functionName@file:line:col"

  // Try Chrome/Node format first
  const chromeMatch = callerLine.match(/at\s+(?:Object\.)?(\S+)\s+\(/)
  if (chromeMatch) return chromeMatch[1]

  // Try Firefox format
  const firefoxMatch = callerLine.match(/^(\S+)@/)
  if (firefoxMatch) return firefoxMatch[1]

  // Fallback: return the trimmed line
  return callerLine.trim()
}

/**
 * Throws if called from within a React component or hook. NOT IN PRODUCTION.
 * Use this to guard utilities that must run outside React context.
 * Automatically detects the calling function name from the stack trace.
 */
export function assertNotInReactContext(): void {
  if (process.env.NODE_ENV !== 'production' && isWithinReactComponentOrHook()) {
    const callerName = getCallerFunctionName()
    throw new Error(
      `${callerName} must not be called from within a React component or hook. ` +
        `Move this call outside of your component to the module scope.`
    )
  }
}

const omitUndefined = omitBy((value: any) => value === undefined)

export function cloneSafely(element: any, props: Record<string, any>, optionalProps: Record<string, any> = {}) {
  if (isValidElement(element))
    return cloneElement(element, {
      ...props,
      ...omitUndefined(optionalProps)
    })
  return element
}
